/**
 * Preparing a photo for the discussion, in the browser.
 *
 * Every photo is redrawn onto a canvas and re-encoded before upload. That
 * does three things at once: shrinks a 12-megapixel phone photo to something
 * a tower-site link can carry, applies the camera's rotation (browsers honour
 * EXIF orientation when drawing an <img>), and drops the EXIF itself --
 * including the GPS position of wherever it was taken. The server strips
 * metadata again regardless; this is the half that also fixes size.
 */

/** Longest side of the stored photo. Plenty to read a label on a breaker. */
export const PHOTO_SIDE = 2048;
/** Longest side of the thumbnail shown in the conversation. */
export const THUMB_SIDE = 480;
export const MAX_PHOTOS = 4;
/** Matches the server's limit. */
const MAX_BYTES = 4 * 1024 * 1024;

/** Dimensions scaled to fit within `max` on the long side, never enlarged. */
export function fitWithin(width: number, height: number, max: number): { width: number; height: number } {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/**
 * PNG stays PNG -- a screenshot of a terminal turns to mush as JPEG -- unless
 * that makes it too big, which a PNG *photo* will. Everything else is JPEG.
 */
export function outputType(sourceType: string, pngBytes: number | null): "image/png" | "image/jpeg" {
  return sourceType === "image/png" && pngBytes !== null && pngBytes <= MAX_BYTES ? "image/png" : "image/jpeg";
}

export interface PreparedPhoto {
  data_base64: string;
  thumb_base64: string;
  /** An object URL for the preview; revoke it when done. */
  preview: string;
}

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("This browser cannot read that image. Try a JPEG or PNG."));
    };
    img.src = url;
  });
}

function encode(img: HTMLImageElement, max: number, type: "image/png" | "image/jpeg"): Promise<Blob> {
  const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, max);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return Promise.reject(new Error("Could not prepare the image"));
  if (type === "image/jpeg") {
    // JPEG has no transparency; without a fill a transparent PNG goes black.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
  }
  context.drawImage(img, 0, 0, width, height);
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not encode the image"))), type, 0.85),
  );
}

async function toBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  // Chunked: String.fromCharCode with a few MB of arguments overflows the stack.
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  const img = await loadImage(file);
  let full = file.type === "image/png" ? await encode(img, PHOTO_SIDE, "image/png") : null;
  if (!full || outputType(file.type, full.size) === "image/jpeg") full = await encode(img, PHOTO_SIDE, "image/jpeg");
  if (full.size > MAX_BYTES) throw new Error("That image is too large even after resizing");
  const thumb = await encode(img, THUMB_SIDE, "image/jpeg");
  return { data_base64: await toBase64(full), thumb_base64: await toBase64(thumb), preview: URL.createObjectURL(thumb) };
}
